import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { determineAppEnvironment } from "../EzEnviornmentInfo/EzEnviornmentInfo";
import Footer from '../footer/footer';
import { CapterraLogo, G2Logo } from './Logos';
import { ratingPlatforms } from '../../config/thirdPartyLinks';
import './Rating.scss';

const Rating = ({ defaultPlatform = 'capterra' }) => {
    const params = useParams();
    const platform = (params.platform || defaultPlatform).toLowerCase();

    // States
    const [score, setScore] = useState(null);
    const [view, setView] = useState('rating'); // 'rating', 'invite', 'feedback', 'success'

    // Feedback form states
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [message, setMessage] = useState('');
    const [consent, setConsent] = useState(true);

    // Validation states
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const redirectDomain = determineAppEnvironment();

    useEffect(() => {
        document.title = `ezClocker: Rate Us on ${platform === 'g2' ? 'G2' : 'Capterra'}`;
        window.scrollTo(0, 0);
    }, [platform]);

    // Handles Score Selection
    const handleScoreSelect = (selectedScore) => {
        setScore(selectedScore);

        // Gating logic: 9-10 goes to public review prompt; 0-6 goes to internal feedback form; 7-8 goes straight to success
        if (selectedScore >= 9) {
            setView('invite');
        } else if (selectedScore >= 0 && selectedScore <= 6) {
            setErrors({});
            setView('feedback');
        } else {
            setView('success');
        }
    };

    // Validates feedback form
    const validateForm = () => {
        const tempErrors = {};
        if (!name.trim()) tempErrors.name = 'Please provide your name';

        const emailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        if (!email.trim()) {
            tempErrors.email = 'Please provide your email';
        } else if (!emailRegex.test(email.toLowerCase())) {
            tempErrors.email = 'Please provide a valid email';
        }

        const phoneRegex = /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;
        if (phone.trim() && !phoneRegex.test(phone)) {
            tempErrors.phone = 'Please provide a valid phone number or leave it blank';
        }

        setErrors(tempErrors);
        return Object.keys(tempErrors).length === 0;
    };

    // Submits feedback form
    const handleFormSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSubmitting(true);
        // Clear any previous submit error
        setErrors(prev => {
            const { submit, ...rest } = prev;
            return rest;
        });

        try {
            const isLocalOrDev = window.location.hostname === 'localhost' ||
                window.location.hostname.includes('127.0.0.') ||
                redirectDomain.includes('dev.ezclocker.com');
            const apiUrl = isLocalOrDev ? '/api/v1/rating' : `${redirectDomain}/api/v1/rating`;

            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    rating: score,
                    name: name,
                    phone: phone,
                    email: email,
                    message: message,
                    consent: consent ? 'YES' : 'NO'
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            let data = null;
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
                try {
                    data = await response.json();
                } catch (e) {
                    console.warn('Failed to parse JSON response:', e);
                }
            }

            if (data && data.errorCode !== undefined && data.errorCode !== 0) {
                throw new Error(data.message || 'Server error occurred while submitting feedback.');
            }

            // Clear fields on success
            setName('');
            setEmail('');
            setPhone('');
            setMessage('');
            setConsent(true);
            setView('success');
        } catch (error) {
            console.error('Error submitting feedback:', error);
            setErrors(prev => ({
                ...prev,
                submit: 'We encountered an error sending your feedback. Please try again.'
            }));
        } finally {
            setIsSubmitting(false);
        }
    };

    // Links config
    const targetPlatform = ratingPlatforms[platform] || ratingPlatforms.capterra;
    const targetPlatformName = targetPlatform.name;
    const reviewUrl = targetPlatform.reviewUrl;

    return (
        <div className="RatingPage">
            <div className="rating-page-container">
                <div className="rating-card">
                    {/* Header Logo */}
                    <div className="rating-logo-wrap">
                        <Link to="/">
                            <img className="ezc-logo" src={`${redirectDomain}/public/images/logos/logo.svg`} alt="ezClocker logo" />
                        </Link>
                    </div>

                    {/* View 1: NPS Rating Selection */}
                    {view === 'rating' && (
                        <div className="rating-view-container">
                            <h2 className="rating-title">How likely are you to recommend ezClocker to another business owner?</h2>
                            <p className="rating-subtitle">
                                On a scale of 0 to 10, with 10 being extremely likely
                            </p>

                            <div className="nps-scale-container">
                                <div className="nps-grid">
                                    {[...Array(11).keys()].map((num) => {
                                        let ratingClass = 'detractor';
                                        if (num === 7 || num === 8) ratingClass = 'passive';
                                        if (num === 9 || num === 10) ratingClass = 'promoter';

                                        return (
                                            <button
                                                key={num}
                                                type="button"
                                                className={`nps-btn nps-btn-${num} ${ratingClass} ${score === num ? 'selected-nps' : ''}`}
                                                onClick={() => handleScoreSelect(num)}
                                            >
                                                {num}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* View 2: Promoter Review Redirection */}
                    {view === 'invite' && (
                        <div className="promoter-view-container">
                            <h2 className="rating-title">Awesome. Mind sharing that on a software review site?</h2>
                            <p className="rating-subtitle">
                                {targetPlatformName} helps other business owners find tools like ezClocker. Takes about 2 minutes.
                            </p>

                            <div className="review-logo-container">
                                <a href={reviewUrl} target="_blank" rel="noopener noreferrer" className="review-logo-btn">
                                    {platform === 'g2' ? <G2Logo /> : <CapterraLogo />}
                                </a>
                            </div>

                            {platform === 'capterra' && (
                                <div className="review-guidelines">
                                    <h4>From Capterra's Listing:</h4>
                                    <ul>
                                        <li>Click &ldquo;Review this Product!&rdquo;</li>
                                        <li>Give our product an overall star rating and submit your comments (minimum 15 characters).</li>
                                        <li>You don't need to sign up to submit a review, and Capterra will send a confirmation link via email.</li>
                                    </ul>
                                </div>
                            )}

                            {platform === 'g2' && (
                                <div className="review-guidelines">
                                    <h4>From G2's Listing:</h4>
                                    <ul>
                                        <li>Click &ldquo;Write a Review&rdquo; on our product page.</li>
                                        <li>Share what you like most, what you dislike, and how ezClocker helps your business.</li>
                                        <li>G2 reviews require verification to ensure high quality.</li>
                                    </ul>
                                </div>
                            )}

                            <div className="form-actions" style={{ justifyContent: 'center' }}>
                                <a href={reviewUrl} target="_blank" rel="noopener noreferrer" className="action-btn primary-btn">
                                    Review us on {targetPlatformName}
                                </a>
                                <button type="button" className="action-btn secondary-btn" onClick={() => setView('rating')}>
                                    Back
                                </button>
                            </div>
                        </div>
                    )}

                    {/* View 3: Detractor/Passive Internal Feedback Form */}
                    {view === 'feedback' && (
                        <div className="feedback-view-container">
                            <h2 className="rating-title">Sorry to hear that. What's one thing we could improve?</h2>
                            <p className="rating-subtitle">
                                Customer satisfaction is our number one priority. Please let us know how we can improve your experience.
                            </p>

                            <form className="feedback-form" onSubmit={handleFormSubmit}>
                                <div className="form-group">
                                    <label htmlFor="name">Your Name *</label>
                                    <input
                                        type="text"
                                        id="name"
                                        value={name}
                                        onChange={(e) => {
                                            setName(e.target.value);
                                            if (errors.name) setErrors(prev => ({ ...prev, name: null }));
                                        }}
                                        placeholder="Enter your name"
                                    />
                                    {errors.name && <span className="error-message">{errors.name}</span>}
                                </div>

                                <div className="form-group">
                                    <label htmlFor="email">Email Address *</label>
                                    <input
                                        type="email"
                                        id="email"
                                        value={email}
                                        onChange={(e) => {
                                            setEmail(e.target.value);
                                            if (errors.email) setErrors(prev => ({ ...prev, email: null }));
                                        }}
                                        placeholder="Enter your email"
                                    />
                                    {errors.email && <span className="error-message">{errors.email}</span>}
                                </div>

                                <div className="form-group">
                                    <label htmlFor="phone">Phone Number</label>
                                    <input
                                        type="tel"
                                        id="phone"
                                        value={phone}
                                        onChange={(e) => {
                                            setPhone(e.target.value);
                                            if (errors.phone) setErrors(prev => ({ ...prev, phone: null }));
                                        }}
                                        placeholder="Enter your phone number (optional)"
                                    />
                                    {errors.phone && <span className="error-message">{errors.phone}</span>}
                                </div>

                                <div className="form-group">
                                    <label htmlFor="message">Message / Feedback</label>
                                    <textarea
                                        id="message"
                                        value={message}
                                        onChange={(e) => setMessage(e.target.value)}
                                        placeholder="Let us know what we can do better..."
                                    />
                                </div>

                                <div className="checkbox-group" onClick={() => setConsent(!consent)}>
                                    <input
                                        type="checkbox"
                                        id="consent"
                                        checked={consent}
                                        readOnly
                                    />
                                    <span>Yes, you may contact me regarding this feedback.</span>
                                </div>

                                {errors.submit && (
                                    <div className="error-message" style={{ display: 'block', textAlign: 'center', marginBottom: '15px', fontSize: '14px' }}>
                                        {errors.submit}
                                    </div>
                                )}

                                <div className="form-actions">
                                    <button type="button" className="action-btn secondary-btn" style={{ marginLeft: 0 }} onClick={() => {
                                        setErrors({});
                                        setView('rating');
                                    }}>
                                        Skip
                                    </button>
                                    <button type="submit" className="action-btn primary-btn" disabled={isSubmitting}>
                                        {isSubmitting ? 'Sending...' : 'Send Message'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {/* View 4: Feedback Success Screen */}
                    {view === 'success' && (
                        <div className="success-view-container text-center">
                            <div className="success-icon">&#10004;</div>
                            <h2 className="rating-title">Thank You!</h2>
                            <p className="rating-subtitle" style={{ marginBottom: '30px' }}>
                                Your message has been submitted. Thank you for your feedback!
                            </p>
                            <div className="form-actions" style={{ justifyContent: 'center' }}>
                                <Link to="/" className="action-btn primary-btn">
                                    Go to Homepage
                                </Link>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default Rating;
